import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import pipelineService from '../services/pipelineService';
import { API_URL } from '../config/api';
import { hasPermission } from '../utils/permissions';

/**
 * InternalClientStageManager Component
 * 
 * Allows internal users to manage BOTH client interview stages AND internal pipeline stages
 * Supports drag-and-drop reordering
 * 
 * Props:
 * - isInternal: true for internal pipeline stages, false/undefined for client interview stages
 */

const InternalClientStageManager = ({ pipelineId, onClose, onUpdate, isInternal = false }) => {
  const [stages, setStages] = useState([]);
  const [newStageName, setNewStageName] = useState('');
  const [newStageSendsToClient, setNewStageSendsToClient] = useState(false);
  const [newStageIsInterviewing, setNewStageIsInterviewing] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadStages();
  }, [pipelineId, isInternal]);

  const loadStages = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (isInternal) {
        // Load internal pipeline stages
        const response = await axios.get(
          `${API_URL}/pipelines/${pipelineId}/detailed`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setStages(response.data.stages || []);
      } else {
        // Load client interview stages
        const response = await axios.get(
          `${API_URL}/pipelines/${pipelineId}/client-stages`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setStages(response.data.stages || []);
      }
    } catch (err) {
      console.error('Error loading stages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(stages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update positions
    const updatedStages = items.map((item, index) => ({
      ...item,
      position: index + 1
    }));

    setStages(updatedStages);

    // Save to backend
    try {
      if (isInternal) {
        // Use pipelineService for internal stages
        const stageOrder = updatedStages.map(s => ({ id: s.id, position: s.position }));
        await pipelineService.reorderStages(pipelineId, stageOrder);
      } else {
        // Use axios for client stages
        const token = localStorage.getItem('authToken');
        for (const stage of updatedStages) {
          await axios.put(
            `${API_URL}/stages/client/${stage.id}`,
            { name: stage.name, position: stage.position },
            {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
        }
      }
      
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error reordering stages:', err);
      alert('Failed to reorder stages');
      loadStages(); // Reload on error
    }
  };

  const handleCreateStage = async () => {
    if (!newStageName.trim()) return;

    setSaving(true);
    try {
      if (isInternal) {
        // Create internal pipeline stage
        await pipelineService.addStage(pipelineId, { 
          name: newStageName,
          color: '#f3f4f6',
          sends_to_client: newStageSendsToClient,
          is_interviewing_stage: newStageIsInterviewing
        });
      } else {
        // Create client interview stage
        const token = localStorage.getItem('authToken');
        await axios.post(
          `${API_URL}/pipelines/${pipelineId}/client-stages`,
          { name: newStageName, color: '#e3f2fd' },
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }
      
      setNewStageName('');
      setNewStageSendsToClient(false);
      setNewStageIsInterviewing(false);
      await loadStages();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error creating stage:', err);
      alert('Failed to create stage');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStage = async (stageId, newName) => {
    setSaving(true);
    try {
      if (isInternal) {
        // Update internal pipeline stage
        await pipelineService.updateStage(stageId, { name: newName });
      } else {
        // Update client interview stage
        const token = localStorage.getItem('authToken');
        const stage = stages.find(s => s.id === stageId);
        await axios.put(
          `${API_URL}/stages/client/${stageId}`,
          { name: newName, position: stage.position },
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }
      
      setEditingStage(null);
      await loadStages();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating stage:', err);
      alert('Failed to update stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStage = async (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    
    // Check if stage has candidates (different property names for internal vs client)
    const candidateCount = isInternal 
      ? (stage.cards?.length || 0) 
      : (stage.candidates?.length || 0);
      
    if (candidateCount > 0) {
      alert(`Cannot delete "${stage.name}" - it has ${candidateCount} candidate(s). Move them first.`);
      return;
    }

    if (!window.confirm(`Delete "${stage.name}" stage?`)) {
      return;
    }

    setSaving(true);
    try {
      if (isInternal) {
        // Delete internal pipeline stage
        await pipelineService.deleteStage(stageId);
      } else {
        // Delete client interview stage
        const token = localStorage.getItem('authToken');
        await axios.delete(
          `${API_URL}/stages/client/${stageId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }
      
      await loadStages();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error deleting stage:', err);
      alert(err.response?.data?.error || 'Failed to delete stage');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return ReactDOM.createPortal(
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }} className="bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="text-sm text-gray-600">Loading stages...</span>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }} className="bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">
              {isInternal ? 'Manage Pipeline Stages' : 'Manage Interview Stages'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isInternal 
                ? 'Create, edit, and reorder your internal workflow'
                : 'Create, edit, and reorder client interview stages'
              }
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          
          {/* Create New Stage */}
          {hasPermission('manage_stages') && (
          <div className="p-3 bg-gray-50 border rounded-lg space-y-2">
            <p className="text-xs font-semibold text-gray-700">Add New Stage</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateStage()}
                placeholder="Enter stage name..."
                className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={saving}
              />
              <button
                onClick={handleCreateStage}
                disabled={!newStageName.trim() || saving}
                className="px-4 py-2 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Adding...' : 'Add'}
              </button>
            </div>
              
              {isInternal && (
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-[11px] cursor-pointer text-gray-600">
                    <input
                      type="checkbox"
                      checked={newStageSendsToClient}
                      onChange={(e) => setNewStageSendsToClient(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    📧 Sends to Client (visible in client portal)
                  </label>
                  
                  <label className="flex items-center gap-2 text-[11px] cursor-pointer text-gray-600">
                    <input
                      type="checkbox"
                      checked={newStageIsInterviewing}
                      onChange={(e) => setNewStageIsInterviewing(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    🎯 Interviewing Stage (auto-move on client action)
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Existing Stages */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Stages ({stages.length})</p>
              <p className="text-[10px] text-gray-400">Drag to reorder</p>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="stages-list">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-2"
                  >
                    {stages.map((stage, index) => (
                      <Draggable
                        key={stage.id}
                        draggableId={`stage-${stage.id}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`flex items-center gap-2 px-3 py-2.5 border rounded-lg transition-colors ${
                              snapshot.isDragging
                                ? 'bg-blue-50 border-blue-300 shadow-lg'
                                : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {/* Drag Handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="cursor-move text-gray-300 hover:text-gray-500 flex-shrink-0"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                              </svg>
                            </div>

                            {/* Position Number */}
                            <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full text-[10px] font-semibold text-gray-500">
                              {index + 1}
                            </div>

                            {/* Stage Name (Editable) */}
                            <div className="flex-1 min-w-0">
                              {editingStage === stage.id ? (
                                <input
                                  type="text"
                                  defaultValue={stage.name}
                                  autoFocus
                                  onBlur={(e) => {
                                    if (e.target.value.trim() && e.target.value !== stage.name) {
                                      handleUpdateStage(stage.id, e.target.value.trim());
                                    } else {
                                      setEditingStage(null);
                                    }
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.target.blur();
                                    } else if (e.key === 'Escape') {
                                      setEditingStage(null);
                                    }
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-blue-400 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                              ) : (
                                <div>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <p className="text-xs font-medium text-gray-900 truncate">{stage.name}</p>
                                    {isInternal && stage.sends_to_client && (
                                      <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] rounded-full">📧 Client</span>
                                    )}
                                    {isInternal && stage.is_interviewing_stage && (
                                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[9px] rounded-full">🎯 Interview</span>
                                    )}
                                  </div>
                                  <p className="text-[10px] text-gray-400">
                                    {isInternal 
                                      ? `${stage.cards?.length || 0} candidates`
                                      : `${stage.candidates?.length || 0} candidates`
                                    }
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2">
                              {/* Toggle Send to Client Button (Internal only) */}
                              {isInternal && (
                                <button
                                  onClick={async () => {
                                    setSaving(true);
                                    try {
                                      await pipelineService.updateStage(stage.id, { 
                                        sends_to_client: !stage.sends_to_client 
                                      });
                                      await loadStages();
                                      if (onUpdate) onUpdate();
                                    } catch (err) {
                                      console.error('Error toggling send to client:', err);
                                      alert('Failed to update send to client setting');
                                    } finally {
                                      setSaving(false);
                                    }
                                  }}
                                  disabled={saving}
                                  className={`p-2 rounded ${
                                    stage.sends_to_client
                                      ? 'text-green-600 bg-green-50 hover:bg-green-100'
                                      : 'text-gray-400 hover:bg-gray-50'
                                  }`}
                                  title={stage.sends_to_client ? 'Disable sending to client' : 'Enable sending to client'}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                </button>
                              )}
                              
                              {/* Toggle Interviewing Stage Button (Internal only) */}
                              {isInternal && (
                                <button
                                  onClick={async () => {
                                    // Only one stage can be the interviewing stage
                                    if (!stage.is_interviewing_stage) {
                                      const confirmed = window.confirm(
                                        'Set this as the interviewing stage?\n\n' +
                                        'This will automatically move candidates here when they enter the client interview process.\n\n' +
                                        'Any other interviewing stage will be unmarked.'
                                      );
                                      if (!confirmed) return;
                                    }
                                    
                                    setSaving(true);
                                    try {
                                      // If enabling, first disable all other interviewing stages
                                      if (!stage.is_interviewing_stage) {
                                        for (const s of stages) {
                                          if (s.is_interviewing_stage && s.id !== stage.id) {
                                            await pipelineService.updateStage(s.id, { 
                                              is_interviewing_stage: false 
                                            });
                                          }
                                        }
                                      }
                                      
                                      await pipelineService.updateStage(stage.id, { 
                                        is_interviewing_stage: !stage.is_interviewing_stage 
                                      });
                                      await loadStages();
                                      if (onUpdate) onUpdate();
                                    } catch (err) {
                                      console.error('Error toggling interviewing stage:', err);
                                      alert('Failed to update interviewing stage setting');
                                    } finally {
                                      setSaving(false);
                                    }
                                  }}
                                  disabled={saving}
                                  className={`p-2 rounded ${
                                    stage.is_interviewing_stage
                                      ? 'text-purple-600 bg-purple-50 hover:bg-purple-100'
                                      : 'text-gray-400 hover:bg-gray-50'
                                  }`}
                                  title={stage.is_interviewing_stage ? 'Remove interviewing stage marker' : 'Mark as interviewing stage'}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              )}
                              
                              {/* Edit Button */}
                              {hasPermission('manage_stages') && (
                              <button
                                onClick={() => setEditingStage(stage.id)}
                                disabled={saving}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                title="Rename stage"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              )}

                              {/* Delete Button */}
                              {hasPermission('manage_stages') && (
                              <button
                                onClick={() => handleDeleteStage(stage.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Delete stage"
                                disabled={saving}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {stages.length === 0 && (
              <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                No stages yet. Create your first stage above!
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InternalClientStageManager;
